import { Badge, cn, Icon, type TableColumn, Typography } from "@kyan/ui";
import moment from "moment";
import { type ReactNode } from "react";

import { type RfqEntry } from "#/app/(desktop)/layouts/PositionsDrawer/components/Rfq/Rfq.types";
import { tooltips } from "#/config/tooltips";
import { formatInstrumentName } from "#/utils/instrument.utils";
import { formatCompact, formatCurrency } from "#/utils/valueFormatters";

import { RfqResponseAction } from "../RfqResponseAction/RfqResponseAction";
import { RfqRiskChange } from "../RfqRiskChange/RfqRiskChange";

const OptionalRenderer = ({
  value,
  children,
  showNoValue = true,
}: {
  value?: unknown;
  children: ReactNode;
  showNoValue?: boolean;
}) => {
  if (value !== undefined && Boolean(value)) return children;

  return showNoValue ? <Typography variant="secondary">-</Typography> : null;
};

export const innerTableColumns: TableColumn<RfqEntry>[] = [
  {
    id: "instrument",
    minWidth: "220px",
    cellClassName: "pl-10",
    cell: (row) => (
      <div className="flex items-center gap-4">
        {row.variant === "summary" && (
          <Icon
            name="arrow-up"
            className="text-xxs text-textSecondary ml-1.5 rotate-90"
          />
        )}
        <Typography grotesk={row.variant === "summary"}>
          {formatInstrumentName(row.instrument)}
        </Typography>
      </div>
    ),
  },
  {
    id: "side",
    maxWidth: "70px",
    cell: (row) => (
      <OptionalRenderer value={row.side}>
        <Badge variant={row.side} className="capitalize">
          {row.side}
        </Badge>
      </OptionalRenderer>
    ),
  },
  {
    id: "price",
    maxWidth: "80px",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.price}>
        <Typography>
          {formatCurrency(row.price ?? 0, { template: "0.00" })}
        </Typography>
      </OptionalRenderer>
    ),
    tooltip: tooltips.price,
  },
  {
    id: "size",
    maxWidth: "50px",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.size}>
        <Typography>{formatCompact(row.size ?? 0)}</Typography>
      </OptionalRenderer>
    ),
  },

  {
    id: "imr",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.response}>
        <RfqRiskChange isPercent riskMetric="imr" response={row.response!} />
      </OptionalRenderer>
    ),
    minWidth: "130px",
  },
  {
    id: "mmr",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.response}>
        <RfqRiskChange isPercent riskMetric="mmr" response={row.response!} />
      </OptionalRenderer>
    ),
    minWidth: "130px",
  },
  {
    id: "delta",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.response}>
        <RfqRiskChange riskMetric="delta" response={row.response!} />
      </OptionalRenderer>
    ),
    minWidth: "110px",
  },
  {
    id: "gamma",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.response}>
        <RfqRiskChange
          riskMetric="gamma"
          template="0.000000"
          response={row.response!}
        />
      </OptionalRenderer>
    ),
    minWidth: "140px",
  },
  {
    id: "vega",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.response}>
        <RfqRiskChange riskMetric="vega" response={row.response!} />
      </OptionalRenderer>
    ),
    minWidth: "110px",
  },
  {
    id: "theta",
    cell: (row) => (
      <OptionalRenderer showNoValue value={row.response}>
        <RfqRiskChange riskMetric="theta" response={row.response!} />
      </OptionalRenderer>
    ),
    minWidth: "130px",
  },
  {
    id: "netPrice",
    cell: (row) => {
      const isDebit = Boolean(row.netPrice) && (row.netPrice ?? 0) < 0;
      const absValue = Math.abs(row.netPrice ?? 0);

      return (
        <OptionalRenderer showNoValue value={row.netPrice}>
          <div className="flex items-end gap-1">
            {row.variant === "summary" && (
              <div className="grid">
                <Typography size={10} variant="secondary">
                  Total
                </Typography>
                <Typography
                  grotesk
                  className={cn({
                    "text-success": !isDebit,
                    "text-error": isDebit,
                  })}
                >
                  {isDebit ? "Debit" : "Credit"}
                </Typography>
              </div>
            )}
            <Typography>
              {formatCurrency(absValue, { precision: 0 })}
            </Typography>
          </div>
        </OptionalRenderer>
      );
    },
    minWidth: "130px",
  },
  {
    id: "timestamp",
    minWidth: "120px",
    sort: (a, b) => moment(b.time).diff(moment(a.time)),
    cell: (row) => (
      <Typography size={11}>
        {moment(row.time).format("DD-MMM-h:mm")}
      </Typography>
    ),
  },
  {
    id: "action",
    cell: (row) => (
      <OptionalRenderer value={row.response}>
        <RfqResponseAction response={row.response!} />
      </OptionalRenderer>
    ),
  },
];

export const immediateLayerColumns: TableColumn<RfqEntry>[] = [
  {
    id: "instrument",
    header: "Instrument",
    minWidth: "220px",
    cellClassName: "pl-10",
    columnClassName: "pl-10",
    cell: (row, { isRowOpen }) => {
      return (
        <div className={cn("flex w-full items-center font-medium")}>
          <div
            className={cn("bg-success absolute left-7.5 size-1 rounded-full", {
              invisible: row.variant !== "response" || row.opened,
            })}
          />
          <Typography
            className={cn({ "text-textSecondary": row.variant === "response" })}
          >
            {formatInstrumentName(row.instrument)}
          </Typography>
          <Icon
            name="caret-up"
            className={cn(
              "text-xxs mr-2 ml-auto rotate-90 transition-transform",
              {
                "rotate-180": isRowOpen,
                "text-textSecondary": !isRowOpen,
              },
            )}
          />
        </div>
      );
    },
  },
  {
    id: "side",
    maxWidth: "70px",
  },
  {
    id: "price",
  },
  {
    id: "size",
    maxWidth: "50px",
  },
  {
    id: "imr",
  },
  {
    id: "mmr",
  },
  {
    id: "delta",
  },
  {
    id: "gamma",
    minWidth: "150px",
  },
  {
    id: "vega",
  },
  {
    id: "theta",
  },
  {
    id: "netPrice",
    minWidth: "130px",
  },
  {
    id: "time",
    minWidth: "120px",
  },
  {
    id: "action",
  },
];
